import axios from 'axios';
import { authService } from './authService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

export interface PrivacyAgent {
  name: string;
  type: 'realtime' | 'analyst';
  prompt: string;
  enabled: boolean;
  running: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentExecution {
  id: string;
  agentName: string;
  agentType: 'realtime' | 'analyst';
  success: boolean;
  result: any;
  systemPrompt: string;
  durationMs: number;
  duration_ms?: number;  // API may return snake_case
  parse_attempts?: number;  // API may return snake_case
  model: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt: string | null;
  error: string | null;
  inputSummary?: string;
  continuationId?: string | null;
}

class AgentService {
  private getAuthHeaders() {
    const token = authService.getToken();
    return token ? { 'x-access-token': token } : {};
  }

  async getAgents(userId?: string): Promise<{ count: number; agents: Record<string, PrivacyAgent> }> {
    const params = userId ? { user_id: userId } : {};
    const response = await axios.get(`${API_URL}/api/agents`, {
      headers: this.getAuthHeaders(),
      params
    });
    return response.data;
  }

  async getAgent(agentName: string, userId?: string): Promise<PrivacyAgent> {
    const params = userId ? { user_id: userId } : {};
    const response = await axios.get(`${API_URL}/api/agents/${encodeURIComponent(agentName)}`, {
      headers: this.getAuthHeaders(),
      params
    });
    return response.data;
  }

  async saveAgent(
    agentName: string,
    config: { type: string; prompt: string; enabled: boolean; running?: boolean },
    userId?: string
  ) {
    const params = userId ? { user_id: userId } : {};
    const response = await axios.post(
      `${API_URL}/api/agents/${encodeURIComponent(agentName)}`,
      config,
      {
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        params
      }
    );
    return response.data;
  }

  async deleteAgent(agentName: string, userId?: string) {
    const params = userId ? { user_id: userId } : {};
    const response = await axios.delete(
      `${API_URL}/api/agents/${encodeURIComponent(agentName)}`,
      {
        headers: this.getAuthHeaders(),
        params
      }
    );
    return response.data;
  }

  async executeAgent(
    agentName: string,
    inputData: string,
    context: Record<string, any> = {},
    userId?: string,
    model?: string
  ): Promise<AgentExecution> {
    const params = userId ? { user_id: userId } : {};
    const response = await axios.post(
      `${API_URL}/api/agents/${encodeURIComponent(agentName)}/execute`,
      { ...(inputData ? { input_data: inputData } : {}), context, ...(model ? { model } : {}) },
      {
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        params,
        // Must exceed max agent run + public ALB idle_timeout (long LLM); keep in sync with privacypal-api / ALB settings.
        timeout: 660000
      }
    );
    return response.data;
  }

  async getAgentHistory(
    agentName: string,
    userId?: string,
    limit: number = 50
  ): Promise<{ count: number; executions: AgentExecution[] }> {
    const params: Record<string, any> = { limit };
    if (userId) params.user_id = userId;
    const response = await axios.get(
      `${API_URL}/api/agents/${encodeURIComponent(agentName)}/history`,
      {
        headers: this.getAuthHeaders(),
        params
      }
    );
    return response.data;
  }

  async getAllExecutionHistory(
    userId?: string,
    limit: number = 100
  ): Promise<{ count: number; executions: AgentExecution[] }> {
    const params: Record<string, any> = { limit };
    if (userId) params.user_id = userId;
    const response = await axios.get(
      `${API_URL}/api/agents/executions/all`,
      {
        headers: this.getAuthHeaders(),
        params
      }
    );
    return response.data;
  }

  async getAgentExecutionsForAudit(
    continuationId: string
  ): Promise<{ count: number; executions: AgentExecution[] }> {
    const response = await axios.get(
      `${API_URL}/api/agents/executions/audit/${encodeURIComponent(continuationId)}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export const agentService = new AgentService();
